/**
 * Safe localStorage wrapper that gracefully handles tracking prevention,
 * private browsing mode, and other scenarios where storage access is blocked.
 *
 * When storage is unavailable, operations silently fall back to no-ops/defaults
 * instead of throwing exceptions that would crash the app during bootstrap.
 */

const memoryFallback: Record<string, string> = {};
let storageAvailable: boolean | null = null;

function isStorageAvailable(): boolean {
  if (storageAvailable !== null) return storageAvailable;

  try {
    const testKey = "__storage_test__";
    window.localStorage.setItem(testKey, "1");
    window.localStorage.removeItem(testKey);
    storageAvailable = true;
  } catch {
    storageAvailable = false;
  }

  return storageAvailable;
}

/**
 * A Storage-compatible object that wraps localStorage with try-catch.
 * Falls back to in-memory storage when localStorage is blocked.
 * Can be passed directly to VuexPersistence as `storage`.
 */
export const safeLocalStorage: Storage = {
  get length(): number {
    try {
      if (isStorageAvailable()) return window.localStorage.length;
    } catch {
      // ignore
    }
    return Object.keys(memoryFallback).length;
  },

  key(index: number): string | null {
    try {
      if (isStorageAvailable()) return window.localStorage.key(index);
    } catch {
      // ignore
    }
    return Object.keys(memoryFallback)[index] ?? null;
  },

  getItem(key: string): string | null {
    try {
      if (isStorageAvailable()) return window.localStorage.getItem(key);
    } catch {
      // ignore
    }
    return memoryFallback[key] ?? null;
  },

  setItem(key: string, value: string): void {
    try {
      if (isStorageAvailable()) {
        window.localStorage.setItem(key, value);
        return;
      }
    } catch {
      // ignore
    }
    memoryFallback[key] = value;
  },

  removeItem(key: string): void {
    try {
      if (isStorageAvailable()) {
        window.localStorage.removeItem(key);
        return;
      }
    } catch {
      // ignore
    }
    delete memoryFallback[key];
  },

  clear(): void {
    try {
      if (isStorageAvailable()) {
        window.localStorage.clear();
        return;
      }
    } catch {
      // ignore
    }
    for (const key of Object.keys(memoryFallback)) {
      delete memoryFallback[key];
    }
  }
};

/**
 * Safely get a value from localStorage. Returns defaultValue if storage is blocked.
 */
export function safeGetItem(key: string): string | null {
  return safeLocalStorage.getItem(key);
}

/**
 * Safely set a value in localStorage. No-op if storage is blocked.
 */
export function safeSetItem(key: string, value: string): void {
  safeLocalStorage.setItem(key, value);
}
